/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import deepcopy from 'deepcopy';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, share, takeUntil } from 'rxjs/operators';
import { TicketType, DefaultSplitDataRenderType, Role } from '@touchplan/common';
import {
	mapValuesToArray,
	simpleSortArray,
} from 'src/app/plain-javascript/services/rxjs-internal-extensions';
import {
	NewOrdinalAssignment,
	OrdinalCalculator,
	OrdinalCalculatorFactory,
} from 'src/app/services/OrdinalCalculatorFactory.service';
import { sortCustomField, sortCustomFieldDropdownItem } from 'src/app/utils/array-util';
import { CustomFieldDefinition, ICustomFieldDefinition } from '../models/CustomFieldDefinition';
import {
	CustomFieldDropDownDefinition,
	ICustomFieldDropDownDefinition,
	ICustomFieldDropDownItemDefinition,
} from '../models/CustomFieldDropDownDefinition';
import {
	CustomFieldTicketValue,
	ICustomFieldTicket,
	ICustomFieldTicketValueLocalPayload,
} from '../models/CustomFieldTicketValue';
import { CustomFieldDefinitions } from './CustomFieldDefinitions';
import { CustomFieldDropDownDefinitions } from './CustomFieldDropDownDefinitions';
import { CustomFieldTicketValues } from './CustomFieldTicketValues';
import { analyticsEventsService } from 'src/app/plain-javascript/services/analytics/AnalyticsEventsService';
import * as appStrings from '@touchplan/strings';

interface ICustomFieldDefinitionLocalPayload
	extends Omit<ICustomFieldDefinition, 'label' | 'type' | 'listOfValues' | 'prevData'> {
	label?: string;
	type?: string;
	listOfValues?: ICustomFieldDropDownDefinition;
}

export enum CustomFieldType {
	Text = 'text',
	Number = 'number',
	DropDownList = 'dropDownList',
	Link = 'link',
}

export enum CustomFieldListType {
	Definition,
	DropDownDefinition,
	TicketValue,
	OnDeckTicketValue,
}

// map type to a dataType
interface ICustomFieldDataType {
	[key: string]: string;
}

export class CustomFieldsApi {
	private definitionOrdinalCalculator: OrdinalCalculator<CustomFieldDefinition>;
	private dropDownDefinitionOrdinalCalculator: OrdinalCalculator<ICustomFieldDropDownItemDefinition>;
	private destroy$: Subject<void>;
	private orderedDefinition$: BehaviorSubject<CustomFieldDefinition[]>;
	private orderedDropdownDefinition$: BehaviorSubject<CustomFieldDropDownDefinition[]>;
	private customFieldDataTypeMap: ICustomFieldDataType;

	constructor(
		private customFieldDefinitions: CustomFieldDefinitions,
		private customFieldDropDownDefinitions: CustomFieldDropDownDefinitions, // this is joined into customFieldDefinitions
		public customFieldTicketValues: CustomFieldTicketValues,
		private customFieldOnDeckTicketValues: CustomFieldTicketValues,
		private ordinalCalculatorFactory: OrdinalCalculatorFactory,
		private planId: string
	) {
		this.definitionOrdinalCalculator =
			this.ordinalCalculatorFactory.newIntegerCalculator<CustomFieldDefinition>(
				(obj: CustomFieldDefinition) => obj.view.order
			);
		this.dropDownDefinitionOrdinalCalculator =
			this.ordinalCalculatorFactory.newIntegerCalculator<ICustomFieldDropDownItemDefinition>(
				(obj: ICustomFieldDropDownItemDefinition) => obj.order
			);

		this.destroy$ = new Subject<void>();
		this.orderedDefinition$ = new BehaviorSubject<CustomFieldDefinition[]>([]);
		this.orderedDropdownDefinition$ = new BehaviorSubject<CustomFieldDropDownDefinition[]>([]);
		this.customFieldDataTypeMap = {
			text: 'string',
			number: 'number',
			dropDownList: 'string',
			link: 'string',
		};

		const orderedDefinitionOb$ = this.customFieldDefinitions.data.list$.pipe(
			mapValuesToArray(),
			simpleSortArray(sortCustomField),
			share(),
			takeUntil(this.destroy$)
		);
		const orderedDropdownDefinitionOb$ = this.customFieldDropDownDefinitions.data.list$.pipe(
			mapValuesToArray(),
			map((list: CustomFieldDropDownDefinition[]) => {
				list.forEach((item) => {
					const arr = item.view.data; // we want the pointer to original object, don't copy
					if (arr && arr.length) {
						arr.sort(sortCustomFieldDropdownItem);
					}
				});
				return list;
			}),
			share(),
			takeUntil(this.destroy$)
		);

		orderedDefinitionOb$.subscribe(this.orderedDefinition$);
		orderedDropdownDefinitionOb$.subscribe(this.orderedDropdownDefinition$);
	}

	public createQuantityTrackingCustomFields(allRoles: Role[]) {
		const quantityTrackingData = [
			{
				label: appStrings.QUANTITY_TYPE,
				type: CustomFieldType.DropDownList,
				isEnabled: true,
				listOfValuesId: '',
				dropDownItemLabel: appStrings.DEFAULT_QUANTITY_TYPE,
				ticketTypes: [TicketType.Task, TicketType.Constraint, TicketType.Milestone],
				roles: allRoles.map((role) => role.$id),
				order: this.getNewDefinitionOrder(),
			},
			{
				label: appStrings.QUANTITY_SITE,
				type: CustomFieldType.Text,
				isEnabled: true,
				ticketTypes: [TicketType.Task, TicketType.Constraint, TicketType.Milestone],
				roles: allRoles.map((role) => role.$id),
				order: this.getNewDefinitionOrder(),
			},
			{
				label: appStrings.QUANTITY_PLANNED,
				type: CustomFieldType.Number,
				isEnabled: true,
				ticketTypes: [TicketType.Task, TicketType.Constraint, TicketType.Milestone],
				roles: allRoles.map((role) => role.$id),
				order: this.getNewDefinitionOrder(),
			},
			{
				label: appStrings.QUANTITY_ACTUAL,
				type: CustomFieldType.Number,
				isEnabled: true,
				ticketTypes: [TicketType.Task, TicketType.Constraint, TicketType.Milestone],
				roles: allRoles.map((role) => role.$id),
				order: this.getNewDefinitionOrder(),
			},
		];

		quantityTrackingData.forEach((data) => {
			const list: ICustomFieldDropDownItemDefinition[] = [];
			const newKey = this.addDefinition(data);

			if (data.type === CustomFieldType.DropDownList) {
				list.push({
					isEnabled: true,
					label: data.dropDownItemLabel,
					order: 0,
					local: true,
				});

				this.localUpdateDefinition(newKey, {
					type: CustomFieldType.DropDownList,
					listOfValues: {
						dataType: 'string',
						data: list,
					},
				});
			}

			this.persistDefinition(newKey, DefaultSplitDataRenderType.edit);
			// switch to database view
			this.changeDefinitionView(newKey, DefaultSplitDataRenderType.database);
		});
	}

	public destroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.customFieldDefinitions.destroy();
		this.customFieldDropDownDefinitions.destroy();
		this.customFieldTicketValues.destroy();
		this.ordinalCalculatorFactory = null;
		this.orderedDefinition$.complete();
		this.orderedDropdownDefinition$.complete();
		this.orderedDefinition$ = null;
		this.orderedDropdownDefinition$ = null;
	}

	//============
	// Definitions
	//============
	/**
	 * Returns entire definitions list
	 */
	public getDefinitions(): CustomFieldDefinitions {
		return this.customFieldDefinitions;
	}

	/**
	 * Returns Observable of array, of ordered definitions
	 */
	public getOrderedDefinition$(): Observable<CustomFieldDefinition[]> {
		return this.orderedDefinition$;
	}

	/**
	 * Returns Observable of array, of ordered and filtered definitions based on ticket type, role id and visibility (isEnabled)
	 * @param ticketType ticket type
	 * @param roleId role id, which may be null
	 */
	public getFilteredDefinitionsForTicket$(
		ticketType: TicketType,
		roleId: string
	): Observable<CustomFieldDefinition[]> {
		return this.orderedDefinition$.pipe(
			map((arr: CustomFieldDefinition[]) => {
				return arr.filter((definition: CustomFieldDefinition) => {
					return (
						definition.view.ticketTypes?.includes(ticketType) &&
						definition.view.roles?.includes(roleId) &&
						definition.view.isEnabled
					);
				});
			})
		);
	}

	/**
	 * Returns Observable of array, of ordered dropdown definitions
	 */
	public getOrderedDropdownDefinition$(): Observable<CustomFieldDropDownDefinition[]> {
		return this.orderedDropdownDefinition$;
	}

	/**
	 * Add new definition, invoke this when add Custom Field button is clicked
	 * Also switches the view to edit to allow live editing
	 * @param payload definition payload, including dropdown if it's dropdown list
	 */
	public addDefinition(payload: ICustomFieldDefinitionLocalPayload): string {
		// update the dataType prop
		if (payload.type) {
			payload.dataType = this.customFieldDataTypeMap[payload.type];
		}

		const key: string = this.customFieldDefinitions.data.generateNewChildKey();
		this.customFieldDefinitions.addItem(payload, key);

		// so... since changeView is needed for dropDownDefinition
		// we are preemptively creating id for dropdownDefinition even though it might not be needed
		// this will be cleaned up if type isn't dropDownList
		const dropdownKey: string = this.customFieldDefinitions.data.generateNewChildKey();
		this.customFieldDropDownDefinitions.addItem(payload, dropdownKey);

		// change both views
		const definition = this.customFieldDefinitions.data.syncList.get(key);
		const dropDownDefinition = this.customFieldDropDownDefinitions.data.syncList.get(dropdownKey);
		this.customFieldDefinitions.changeView(definition, DefaultSplitDataRenderType.edit);
		this.customFieldDropDownDefinitions.changeView(dropDownDefinition, DefaultSplitDataRenderType.edit);

		// direct save without localUpdate, no reason to trigger another emission
		this.customFieldDefinitions.data.syncList.get(key).view.listOfValuesId = dropdownKey;

		return key;
	}

	/**
	 * Local update for live edit
	 * @param key CustomFieldDefinition key
	 * @param payload Definition changes
	 */
	public localUpdateDefinition(key: string, payload: ICustomFieldDefinitionLocalPayload): void {
		const definition: CustomFieldDefinition = this.customFieldDefinitions.data.syncList.get(key);
		const dropDownId: string = this.customFieldDefinitions.data.syncList.get(key).view.listOfValuesId;
		const payloadClone: ICustomFieldDefinitionLocalPayload = Object.assign({}, payload);

		/*
		 * data integrity check
		 * 1) new dropdown items need new Firebase ID
		 * 2) remove listOfValues if it exists and type isn't dropDownList
		 */
		if (
			(payload.type && payload.type === CustomFieldType.DropDownList) ||
			definition.view.type === CustomFieldType.DropDownList
		) {
			// generate keys for newly added dropdown items
			if (payload.listOfValues && payload.listOfValues.data.length > 0) {
				for (const item of payload.listOfValues.data) {
					if (item == null) {
						throw Error('Warning! New Custom Field Dropdown item data does not exist');
					}
					if (!item.id) {
						item.id = this.customFieldDropDownDefinitions.data.generateNewChildKey();
					}
				}
			}

			if (payload.listOfValues) {
				this.customFieldDropDownDefinitions.localUpdate(dropDownId, payload.listOfValues);
			}
		}

		// remove dropdown listOfValues if type was changed during creation time
		// checking for payload.type to indicate creation time, cuz this is the only time type can be changed
		if (payload.type && payload.type !== CustomFieldType.DropDownList) {
			// type text or number
			if (payload.listOfValues) {
				// remove listOfValues if it exists
				payloadClone.listOfValuesId = null;
				this.customFieldDropDownDefinitions.removeItem(
					this.customFieldDropDownDefinitions.data.syncList.get(dropDownId)
				);
			}
		}

		delete payloadClone.listOfValues; //don't write this value to the definition, just to the dropdown

		// update the dataType prop
		if (payload.type) {
			payloadClone.dataType = this.customFieldDataTypeMap[payload.type];
		}

		this.customFieldDefinitions.localUpdate(key, payloadClone);
	}

	/**
	 * Save the Custom Field Definitions to Firebase
	 * @param key CustomFieldDefinition key
	 * @param renderType defaults to database view
	 */
	public persistDefinition(key: string, renderType: string = DefaultSplitDataRenderType.database): void {
		const definition: CustomFieldDefinition = this.customFieldDefinitions.data.syncList.get(key);
		const addDefinition = definition.localDataNotYetPersisted;

		// check/remove pre-created listOfValues if type isn't dropDownList
		if (definition.view.listOfValuesId && definition.view.type !== CustomFieldType.DropDownList) {
			if (definition.view.listOfValues) {
				this.customFieldDropDownDefinitions.removeItem(
					this.customFieldDropDownDefinitions.data.syncList.get(definition.view.listOfValuesId)
				);
			}
			delete definition.view.listOfValuesId;
		}

		this.customFieldDefinitions.persistData(definition, renderType);
		if (definition.view.listOfValues) {
			this.customFieldDropDownDefinitions.persistData(
				this.customFieldDropDownDefinitions.data.syncList.get(definition.view.listOfValuesId),
				renderType
			);
		}

		if (addDefinition) {
			analyticsEventsService.defineCustomField(this.planId, definition.$id, definition.view.label);
		} else {
			analyticsEventsService.editCustomField(this.planId, definition.$id, definition.view.label);

			if (definition.didVisibilityChange()) {
				analyticsEventsService.showHideCustomField(
					this.planId,
					definition.$id,
					definition.view.label,
					definition.view.isEnabled
				);
			}
		}
	}

	/**
	 * Switch to a different view, this is for editing mode
	 * @param key CustomFieldDefinition key
	 * @param renderType defaults to edit view
	 */
	public changeDefinitionView(key: string, renderType: string = DefaultSplitDataRenderType.edit): void {
		const definition: CustomFieldDefinition = this.customFieldDefinitions.data.syncList.get(key);
		if (!definition) {
			return;
		}

		this.customFieldDefinitions.changeView(definition, renderType);
		if (definition.view.listOfValues) {
			this.customFieldDropDownDefinitions.changeView(
				this.customFieldDropDownDefinitions.data.syncList.get(definition.view.listOfValuesId),
				renderType
			);
		}
	}

	/**
	 * Cancel Custom Field definition edit, it removes the newly added definition in creation mode
	 * @param key CustomFieldDefinition key
	 * @param renderType defaults to database view
	 */
	public abortDefinitionView(key: string, renderType: string = DefaultSplitDataRenderType.database): void {
		const definition: CustomFieldDefinition = this.customFieldDefinitions.data.syncList.get(key);
		if (!definition) {
			return;
		}

		if (definition.localDataNotYetPersisted) {
			this.customFieldDefinitions.removeItem(definition);
			if (definition.view.listOfValues) {
				this.customFieldDropDownDefinitions.removeItem(definition.view.listOfValues);
			}
			return;
		}

		this.customFieldDefinitions.abortView(definition, renderType);
		if (definition.view.listOfValues) {
			this.customFieldDropDownDefinitions.abortView(
				this.customFieldDropDownDefinitions.data.syncList.get(definition.view.listOfValuesId),
				renderType
			);
		}
	}

	//===================================
	// Ticket Values (& on deck tickets)
	//===================================
	public getCustomFieldTicketValues(
		type: CustomFieldListType = CustomFieldListType.TicketValue
	): CustomFieldTicketValues {
		return this.getTicketValueList(type);
	}

	/**
	 * Add new ticket value
	 * @param ticketId ticket id
	 */
	public addTicketValue(ticketId: string, type: CustomFieldListType = CustomFieldListType.TicketValue): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		if (!list.data.syncList.get(ticketId)) {
			list.addItem({}, ticketId);
		}
	}

	/**
	 * Local update for live edit
	 * @param ticketId ticket id
	 * @param payload ticket value payload
	 */
	public localUpdateTicketValue(
		ticketId: string,
		payload: ICustomFieldTicket,
		type: CustomFieldListType = CustomFieldListType.TicketValue
	): void {
		this.getTicketValueList(type).localUpdate(ticketId, payload);
	}

	/**
	 * Save the Custom Field ticket value to Firebase
	 * @param ticketId ticket id
	 * @param renderType defaults to database view
	 */
	public persistTicketValue(
		ticketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue,
		renderType: string = DefaultSplitDataRenderType.database
	): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const ticketValue: CustomFieldTicketValue = list.data.syncList.get(ticketId);
		for (const [key, value] of Object.entries(ticketValue.view)) {
			// get dataType from definition for new ticketValues
			if (value.value != null && !value.type) {
				const definitionView: ICustomFieldDefinition =
					this.customFieldDefinitions.data.syncList.get(key).view;
				value.type = definitionView.type;
				value.dataType = definitionView.dataType;
			}
		}
		list.persistData(ticketValue, renderType);
	}

	/**
	 * Switch to a different view (for editing)
	 * @param ticketId ticket id
	 */
	public changeTicketValueView(
		ticketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue,
		renderType: string = DefaultSplitDataRenderType.edit
	): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const ticketValue: CustomFieldTicketValue = list.data.syncList.get(ticketId);
		if (ticketValue) {
			list.changeView(ticketValue, renderType);
		}
	}

	/**
	 * Abort Custom Field ticket value edit, invoke this when Cancel button is clicked
	 * @param ticketId ticket id
	 * @param renderType defaults to database view
	 */
	public abortTicketValueView(
		ticketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue,
		renderType: string = DefaultSplitDataRenderType.database
	): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const ticketValue: CustomFieldTicketValue = list.data.syncList.get(ticketId);
		if (!ticketValue) {
			return;
		}

		if (ticketValue.localDataNotYetPersisted) {
			// local data that should be removed
			list.removeItem(ticketValue);
		} else {
			list.abortView(ticketValue, renderType);
		}
	}

	//=======
	// utils
	//=======
	/**
	 * Check new label to be created against all existing ones for uniqueness
	 * @param sourceLabel  label to be checked for uniqueness
	 * @param sourceDefinition optional custom definition whose label is the sourceLabel
	 */
	public isDefinitionLabelUnique(sourceLabel: string, sourceDefinition?: CustomFieldDefinition): boolean {
		let isUnique = true;
		this.customFieldDefinitions.data.syncList.forEach((value: CustomFieldDefinition) => {
			if (sourceDefinition && sourceDefinition.$id === value.$id) {
				return;
			}
			if (value?.view?.label?.toLocaleLowerCase().trim() === sourceLabel.toLocaleLowerCase().trim()) {
				isUnique = false;
			}
		});
		return isUnique;
	}

	/**
	 * Create ticket value, e.g. when a ticket is copied to a new plan
	 * Shorthand for add, localUpdate and persist
	 * NOTE: this could also delete the custom field data if payload is null
	 * @param ticketId new ticket id
	 * @param payload ticket value payload
	 */
	public createCustomFieldTicketValues(
		ticketId: string,
		payload: ICustomFieldTicket,
		type: CustomFieldListType = CustomFieldListType.TicketValue
	): void {
		this.addTicketValue(ticketId, type);
		this.localUpdateTicketValue(ticketId, payload, type);
		this.persistTicketValue(ticketId, type);
	}

	/**
	 * Duplicate ticket value, e.g. when a ticket is duplicated within same plan
	 * @param originalTicketId original ticket id
	 * @param newTicketId new ticket id
	 */
	public duplicateCustomFieldTicketValues(
		originalTicketId: string,
		newTicketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue
	): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const ticketValue = list.data.syncList.get(originalTicketId) as ICustomFieldTicketValueLocalPayload;
		if (!ticketValue) {
			return; // nothing to copy...
		}

		this.createCustomFieldTicketValues(
			newTicketId,
			this.cloneCustomFieldTicketValues(originalTicketId, type),
			type
		);
	}

	/**
	 * Return the deep copy of CustomFieldTicketValue object
	 * @param ticketId ticket id
	 */
	public cloneCustomFieldTicketValues(
		ticketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue,
		deepCopy: boolean = true
	): ICustomFieldTicket {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const customFieldTicketValue = list.data.syncList.get(ticketId)?.view;
		const data = customFieldTicketValue ? this.getTicketValueClone(customFieldTicketValue, deepCopy) : null;
		return data;
	}

	/**
	 * Revert given ticket values data to database
	 * This is needed when role changes in ticket edit, revert previous edited custom fields
	 * @param ticketId ticket id
	 * @param customFieldDefinitionIds list of ticket values to be reverted
	 */
	public revertTicketValues(ticketId: string, customFieldDefinitionIds: string[]): void {
		const ticketValues: CustomFieldTicketValue = this.customFieldTicketValues.data.syncList.get(ticketId);
		const payload: ICustomFieldTicket = {};
		// ticket doesn't exist
		if (!ticketValues) {
			return;
		}

		for (const id of customFieldDefinitionIds) {
			payload[id] = Object.assign({}, ticketValues.getDatabaseFields()[id]);
		}
		this.customFieldTicketValues.localUpdate(ticketId, payload);
	}

	/**
	 * Get order number for new definition, call this before addDefinition()
	 * @param addToLast flag to indicate whether generate order number for first or last of the list, default to last
	 */
	public getNewDefinitionOrder(addToLast: boolean = true): number {
		if (addToLast) {
			return this.definitionOrdinalCalculator.newOrdinalToAppend(
				Array.from(this.customFieldDefinitions.data.syncList.values()).sort(sortCustomField)
			);
		} else {
			return this.definitionOrdinalCalculator.newOrdinalToPrepend(
				Array.from(this.customFieldDefinitions.data.syncList.values()).sort(sortCustomField)
			);
		}
	}

	/**
	 * Get order number for new dropdown item, call this before localUpdateDefinition()
	 * @param listOfValuesId listOfValuesId of new order number to be created for
	 * @param addToLast flag to indicate whether generate order number for first or last of the list, default to last
	 */
	public getNewDropDownItemOrder(listOfValuesId: string = null, addToLast: boolean = true): number {
		const dropdownDefinitions =
			this.customFieldDropDownDefinitions.data.syncList.get(listOfValuesId)?.view?.data;
		if (!listOfValuesId || !dropdownDefinitions) {
			return 0;
		}

		if (addToLast) {
			return this.dropDownDefinitionOrdinalCalculator.newOrdinalToAppend(dropdownDefinitions);
		} else {
			return this.dropDownDefinitionOrdinalCalculator.newOrdinalToPrepend(dropdownDefinitions);
		}
	}

	/**
	 * Update order of moved custom field definition item
	 * @param previousIndex previous definition index in array
	 * @param currentIndex current definition index in array
	 */
	public moveDefinition(previousIndex: number, currentIndex: number): void {
		const definitionArr: CustomFieldDefinition[] = Array.from(
			this.customFieldDefinitions.data.syncList.values()
		).sort(sortCustomField);
		let results = this.definitionOrdinalCalculator.calcNewOrdinalsForMove(
			definitionArr,
			previousIndex,
			1,
			currentIndex
		) as Array<NewOrdinalAssignment<CustomFieldDefinition>>;
		// localUpdate current definition
		results.forEach((item) => {
			this.customFieldDefinitions.localUpdate(item.object.$id, { order: item.ordinal });
		});

		// check for all definitions, update results if entire list needs renumbering
		definitionArr.sort(sortCustomField);
		if (!this.definitionOrdinalCalculator.ordinalsAreValidMinimallySpacedAndSorted(definitionArr)) {
			results = this.definitionOrdinalCalculator.renumberList(definitionArr);
			results.forEach((item) => {
				// since definitions are already ordered, no need to trigger more emissions, set data directly
				item.object.view.order = item.ordinal;
			});
		}

		// persist either dragged definition or entire list
		this.persistDefinitionOrderingChanges(results);
	}

	/**
	 * Moves a custom field definition from its current position to the position of a target definition,
	 * based on their references. The method finds the indices of the dragged and target items in the
	 * sorted list of custom field definitions and moves the dragged item to the target's index.
	 *
	 * @param draggedItem - The custom field definition to be moved.
	 * @param targetItem - The custom field definition that indicates the target position.
	 */
	public moveDefinitionByReference(
		draggedItem: CustomFieldDefinition,
		targetItem: CustomFieldDefinition
	): void {
		const definitionArr: CustomFieldDefinition[] = Array.from(
			this.customFieldDefinitions.data.syncList.values()
		).sort(sortCustomField);
		// We need to find the original index of the dragged item and the target item
		const draggedItemIndex = definitionArr.findIndex((item) => item.$id === draggedItem.$id);
		const targetItemIndex = definitionArr.findIndex((item) => item.$id === targetItem.$id);
		// We then move the dragged item to the target index in the unfiltered list
		this.moveDefinition(draggedItemIndex, targetItemIndex);
	}

	/**
	 * Checks that the ordering values (the ordinals) of the custom field definitions are valid
	 * i.e. don't contain duplicates or floating point values, and if not, fixes them and
	 * persists the fixed ordinal values.
	 */
	public makeSureDefinitionOrdinalsAreValid(): void {
		const definitionArr: CustomFieldDefinition[] = Array.from(
			this.customFieldDefinitions.data.syncList.values()
		).sort(sortCustomField);
		let results = [] as Array<NewOrdinalAssignment<CustomFieldDefinition>>;

		// check for all definitions, update results if entire list needs renumbering
		if (!this.definitionOrdinalCalculator.ordinalsAreValidMinimallySpacedAndSorted(definitionArr, false)) {
			results = this.definitionOrdinalCalculator.renumberList(definitionArr);
			results.forEach((item) => {
				// since definitions are already ordered, no need to trigger more emissions, set data directly
				item.object.view.order = item.ordinal;
			});
		}

		// persist either moved definition or entire list
		this.persistDefinitionOrderingChanges(results);
	}

	/**
	 * Returns Observable of array, of ordered definitions that are enabled
	 **/
	public getCustomFieldDefinitionsEnabled() {
		return this.getOrderedDefinition$().pipe(
			map((customFieldDefinitions) =>
				customFieldDefinitions
					.filter((customField) => customField.view.isEnabled)
					.map((customFieldItem) => ({
						id: customFieldItem.$id,
						label: customFieldItem.view.label,
					}))
			)
		);
	}

	private persistDefinitionOrderingChanges(
		ordinalAssignments: Array<NewOrdinalAssignment<CustomFieldDefinition>>
	): void {
		ordinalAssignments.forEach((item) => {
			if (!item.object.localDataNotYetPersisted) {
				if (item.object.currentRenderType === DefaultSplitDataRenderType.edit) {
					/*
					 * this is a special case, existing custom field is being edited and reordered
					 * since it's on edit view, we need to patch the data in order to persist order ONLY:
					 * 1) update database view with new order number
					 * 2) persist with database view
					 */
					item.object.getDatabaseFields().order = item.ordinal;
					this.customFieldDefinitions.persistData(item.object, DefaultSplitDataRenderType.database);
				} else if (item.object.currentRenderType === DefaultSplitDataRenderType.database) {
					this.customFieldDefinitions.persistData(item.object);
				}
			}
		});
	}

	/**
	 * Checks that the ordering values (the ordinals) of the given ICustomFieldDropDownItemDefinition
	 * are valid i.e. don't contain duplicates or floating point values, and if not, fixes them.
	 * @return true if fixes were applied; false otherwise
	 */
	public fixDropdownChoiceOrdinalsIfNecessary(
		dropdownDefinitions: ICustomFieldDropDownItemDefinition[]
	): boolean {
		const definitionArr: ICustomFieldDropDownItemDefinition[] =
			dropdownDefinitions.sort(sortCustomFieldDropdownItem);
		let results = [] as Array<NewOrdinalAssignment<ICustomFieldDropDownItemDefinition>>;

		// check for all definitions, update results if entire list needs renumbering
		if (
			!this.dropDownDefinitionOrdinalCalculator.ordinalsAreValidMinimallySpacedAndSorted(definitionArr, false)
		) {
			results = this.dropDownDefinitionOrdinalCalculator.renumberList(definitionArr);
			results.forEach((item) => {
				// since definitions are already ordered, no need to trigger more emissions, set data directly
				item.object.order = item.ordinal;
			});
			return true;
		}

		return false;
	}

	/**
	 * Update order of moved dropdown item
	 * @param listOfValuesId listOfValuesId to update
	 * @param previousIndex previous dropdown item index in array
	 * @param currentIndex current dropdown item index in array
	 */
	public moveDropdownItem(listOfValuesId: string, previousIndex: number, currentIndex: number): void {
		const dropDownDefinition: CustomFieldDropDownDefinition =
			this.customFieldDropDownDefinitions.data.syncList.get(listOfValuesId);
		const dropDownDefinitionArr: ICustomFieldDropDownItemDefinition[] =
			dropDownDefinition.view.data.sort(sortCustomFieldDropdownItem);
		const result = this.dropDownDefinitionOrdinalCalculator.calcNewOrdinalsForMove(
			dropDownDefinitionArr,
			previousIndex,
			1,
			currentIndex
		);
		const clone = [...dropDownDefinitionArr];
		clone[previousIndex].order = result[0].ordinal;
		this.customFieldDropDownDefinitions.localUpdate(dropDownDefinition.$id, { data: clone });

		// check for rest of dropdown items in this list
		dropDownDefinitionArr.sort(sortCustomFieldDropdownItem);
		if (
			!this.dropDownDefinitionOrdinalCalculator.ordinalsAreValidMinimallySpacedAndSorted(
				dropDownDefinitionArr
			)
		) {
			const results = this.dropDownDefinitionOrdinalCalculator.renumberList(dropDownDefinitionArr);
			results.forEach((item) => {
				// since dropdown items are already ordered, no need to trigger more emissions, set data directly
				item.object.order = item.ordinal;
			});
		}
	}

	/**
	 * Move ticket custom field values when tickets(s) are dragged from on deck plan, or vice versa
	 * @param srcTicketId source ticket id
	 * @param destTicketId destination ticket id
	 * @param srcTicketListType source ticket list type, defaults to CustomFieldListType.OnDeckTicketValue
	 * @param destTicketListType destination ticket list type, defaults to CustomFieldListType.TicketValue
	 */
	public moveTicketCustomFieldsBetweenLists(
		srcTicketId: string,
		destTicketId: string,
		srcTicketListType: CustomFieldListType = CustomFieldListType.OnDeckTicketValue,
		destTicketListType: CustomFieldListType = CustomFieldListType.TicketValue
	): void {
		const srcTicketList = this.getTicketValueList(srcTicketListType);
		const destTicketList = this.getTicketValueList(destTicketListType);
		const srcTicketValue = srcTicketList.data.syncList.get(srcTicketId);

		// source ticket has no custom fields data, nothing to move
		if (!srcTicketValue) {
			return;
		}

		// create/update entry
		if (!destTicketList.data.syncList.get(destTicketId)) {
			destTicketList.addItem(srcTicketValue.view, destTicketId);
		} else {
			destTicketList.localUpdate(destTicketId, srcTicketValue.view);
		}

		// persist data
		destTicketList.persistData(destTicketList.data.syncList.get(destTicketId));

		// remove custom fields data from plan ticket entry
		srcTicketList.removeItem(srcTicketValue);
	}

	//===========
	// temporary
	//===========
	/*
	 * This function is temporary, and should be removed when tickets are no longer deleted
	 */
	public removeCustomFieldTicketValue(
		ticketId: string,
		type: CustomFieldListType = CustomFieldListType.TicketValue
	): void {
		const list: CustomFieldTicketValues = this.getTicketValueList(type);
		const ticketValue: CustomFieldTicketValue = list.data.syncList.get(ticketId);
		// given ticket might not have custom field ticket values data
		if (ticketValue) {
			list.removeItem(ticketValue);
		}
	}

	private getTicketValueList(type: CustomFieldListType): CustomFieldTicketValues {
		let list: CustomFieldTicketValues;
		switch (type) {
			case CustomFieldListType.TicketValue:
				list = this.customFieldTicketValues;
				break;
			case CustomFieldListType.OnDeckTicketValue:
				list = this.customFieldOnDeckTicketValues;
				break;
		}
		return list;
	}

	private getTicketValueClone(value: ICustomFieldTicket, deepCopy: boolean = true): ICustomFieldTicket {
		return deepCopy ? deepcopy(value) : Object.assign({}, value);
	}
}

// SAMPLES

// -----create new definition-----
// use this to check if definition label is unique before this.addDefinition()
/*
this.isDefinitionLabelUnique('Custom Field blah')

// add new definition, key is used for all subsequent operations, invoke this when +Custom Field btn is clicked
const key = this.addDefinition({
	label: 'Custom Field blah',
	type: CustomFieldType.DropDownList,
	dataType: 'string',
	isEnabled: true,
	ticketTypes: [
		'task',
		'milestone',
		'constraint'
	]
});

// update when reactive form change triggered, below example also applies to editing definition
this.localUpdateDefinition(key, {
	label: 'Custom Field blah blah blah',
});

// new dropdown item added, keep the format, pass in entire listOfValues object since data is an array
this.localUpdateDefinition(key, {
	listOfValues: {
		dataType: 'string', // always needed, this might be needed for backend in the future
		data: [
			{
				label: 'pipe',
				isEnabled: true,
				order: 0
			},
			{
				label: 'tube',
				isEnabled: true,
				order: 1
			},
			{
				label: 'tunnel',
				isEnabled: true,
				order: 2
			}
		]
	}
});

// change view pointer back to database before persist
this.changeDefinitionView(key, DefaultSplitDataRenderType.database);

// persist when save is clicked
this.persistDefinition(key);

// NOTE! current usage in custom-field-editor
this.persistDefinition(key, DefaultSplitDataRenderType.edit);
this.changeDefinitionView(key, DefaultSplitDataRenderType.database);
// it's persisting using edit view and then switch the view pointer
// this allows the API to diff the value and dispatch corresponding analytic events

// discard changes when cancel is clicked
this.abortDefinitionView(key);

// -----edit existing custom field-----
// change view pointer, invoke this when edit view is open
this.changeDefinitionView(key);

// update changes when reactive form change triggers
this.localUpdateDefinition(key, payload);

// change view pointer back to database before persist
this.changeDefinitionView(key, DefaultSplitDataRenderType.database);

// save edit changes when save is clicked
this.persistDefinition(key);

// discard edited changes
this.abortDefinitionView(key);

// ticket values
// UI doesn't care if it's stringValue or numericValue, API converts it to a property named value
// On save, API automatically the value back into numericValue or stringValue based on definition dataType

// invoked when ticket edit is open, it checks if entry already exists or not, create a new one if not...
this.addTicketValue('-MQEB6i2eq6g6KJv_pV8');

// change view pointer, invoke right after addTicketValue() to allow proper abort
this.changeTicketValueView('-MQEB6i2eq6g6KJv_pV8');

// use localUpdate, only updates changed custom fields, only pass in changed props
this.localUpdateTicketValue('-MQEB6i2eq6g6KJv_pV8', { // <-- ticket id
	'-MKotoNe3SYMx-715ElO': { // <--  custom field id
		value: 12345
	}
});

this.localUpdateTicketValue('-MQEB6i2eq6g6KJv_pV8', { // <-- ticket id
	'-MKotoNe3SYMx-713ElO': { // <--  custom field id
		value: 'test'
	}
});

// ----- save -----
// change view pointer back to database before persist
this.changeTicketValueView('-MQEB6i2eq6g6KJv_pV8', DefaultSplitDataRenderType.database);

// persist
this.persistTicketValue('-MQEB6i2eq6g6KJv_pV8');

// ----- cancel -----
// cancel changes
this.abortTicketValueView('-MQEB6i2eq6g6KJv_pV8');
*/

// -----move data between plan & on deck-----
// on deck -> plan
// this.moveTicketCustomFieldsBetweenLists('floating-1-', '-MUA6vwq6Rx2hBZJXAU0');
// plan -> on deck
// this.moveTicketCustomFieldsBetweenLists('-MUA6vwq6Rx2hBZJXAU0', 'floating-1-', CustomFieldListType.TicketValue, CustomFieldListType.OnDeckTicketValue);
